import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalPersonalizationComponent } from '@app/shared/components/modals/modal-personalization/modal-personalization.component';
import { UsersService } from '@vestiaire/api-sdk-angular';
import { UserSearchPreferencesUniverse } from '@vestiaire/api-sdk-angular/model/userSearchPreferencesUniverse';
import { UserSearchPreferences } from '@vestiaire/api-sdk-angular/model/userSearchPreferences';
import { Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import { SetPersonalization, SetPersonalizationToggle, TrackPersonalization } from '@app/+catalog/store';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'vc-catalog-personalization',
  templateUrl: './catalog-personalization.component.html',
  styleUrls: ['./catalog-personalization.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CatalogPersonalizationComponent implements OnInit, OnChanges {
  @Input() personalizationIsApplicable = false;
  @Input() isCampaign = false;

  public showRefinementBody = true;
  public hasUserPreferences = false;
  public personalizationActive = true;
  public error = false;
  public universeTag: UserSearchPreferencesUniverse;
  public categoryTags = [];
  private persoPref: UserSearchPreferences;

  constructor(
    private dialogService: MatDialog,
    private userServices: UsersService,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store<IAppState>,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.checkQueryParams();
    this.getPreferences();
  }

  ngOnChanges({ isCampaign }: SimpleChanges) {
    if (!isCampaign.firstChange) {
      this.checkQueryParams();
    }
  }

  public togglePersonalization(forcedValue?: boolean) {
    this.personalizationActive = forcedValue ?? !this.personalizationActive;
    this.store.dispatch(new SetPersonalizationToggle(this.personalizationActive));

    // Do not send tracking when we force value. ex: queryParam
    if (typeof forcedValue === 'undefined') {
      this.trackTogglePersonalization();
    }
  }

  public toggleShowBody() {
    this.showRefinementBody = !this.showRefinementBody;
  }

  public openPersonalization(edit: UserSearchPreferences | null = null) {
    this.trackPersonalizationOpen();

    const options = {
      maxWidth: '740px',
      data: {
        userPreferences: edit
      }
    };

    this.dialogService
      .open(ModalPersonalizationComponent, options)
      .afterClosed()
      .subscribe({
        next: () => {
          this.getPreferences();
          this.removeQueryParam();
        }
      });
  }

  _setPersoTags(userPreferences: UserSearchPreferences) {
    this.persoPref = userPreferences;
    this.hasUserPreferences = this.persoPref?.universe.ids.length > 0;
    this.universeTag = this.persoPref?.universe;
    this.categoryTags = this.persoPref?.size_categories;
    this.changeDetectorRef.markForCheck();
  }

  getPreferences() {
    this.userServices.getSearchPreferences().subscribe({
      next: ({ data }) => {
        this._setPersoTags(data);
        this.store.dispatch(new SetPersonalizationToggle(this.personalizationActive));
        this.store.dispatch(new SetPersonalization(data));
      },
      error: () => {
        this.error = true;
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  /**
   * openPersonalization => open personalization modal
   *
   * campaign => perso OFF
   * campaign + enableMySizes:0 => perso OFF
   * campaign + enableMySizes:1 => perso ON
   */
  checkQueryParams() {
    const { openPersonalization, enableMySizes } = this.activatedRoute.snapshot.queryParams;

    if (openPersonalization) {
      this.openPersonalization();
    }

    if (this.isCampaign) {
      this.togglePersonalization(enableMySizes === '1');
    }
  }

  /**
   * If queryParam: openPersonalization exist remove it
   * => not reopen onboarding when user come back
   */
  removeQueryParam() {
    if (this.activatedRoute.snapshot.queryParams.openPersonalization) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          openPersonalization: null
        },
        queryParamsHandling: 'merge'
      });
    }
  }

  trackTogglePersonalization() {
    this.store.dispatch(
      new TrackPersonalization({
        event: 'preferences_enabled',
        eventAction: 'regular filters',
        eventLabel: this.personalizationActive ? 'enable' : 'disable'
      })
    );
  }

  trackPersonalizationOpen() {
    const isEdit = !!this.persoPref?.filter;
    this.store.dispatch(
      new TrackPersonalization({
        event: isEdit ? 'edit_preferences_open' : 'onboarding_preferences_open',
        eventAction: isEdit ? 'edit perso' : 'onboarding',
        eventLabel: 'open'
      })
    );
  }
}
